import React, { createRef } from "react";
import Layout from "../../components/layout/layout";
import LandingHero from "../../components/hero-landing/hero-landing";
import HeroVideo from "../../components/hero-video/hero-video";
import HeroCentered from "../../components/hero-centered/hero-centered";
import HeroAdvanced from "../../components/hero-advanced/hero-advanced";
import LandingHeroV2 from "../../components/hero-landing-v2/hero-landing-v2";
import { graphql, HeadProps, PageProps } from "gatsby";
import {
  HeroLandingSliceFragment,
  StandardLandingPageQuery,
  HeroCenteredSliceFragment,
  HeroFormSliceFragment,
  StandardLandingPageBreadcrumbFragment,
  LocalesSliceFragment,
  SeoSliceFragment,
  NavigationTitleSliceFragment,
  CustomerRfqCtaSlice,
  HeroVideoSliceFragment,
  HeroAdvancedSliceFragment,
  HeroLandingV2SliceFragment,
  PrismicStandardLandingPageData,
  PrismicAnnouncementData,
  PrismicAnnouncement,
  PrismicRichTextField,
} from "../../types/graphql";
import {
  SliceWrapper,
  FullWidthSliceWrapper,
} from "./standard-landing-page.styled";
import HeroForm from "../../components/hero-form/hero-form";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";
import { BreadCrumbProps } from "../../components/breadcrumbs/breadcrumbs";
import { linkTo } from "../../utils/contentUtils";
import { ILocalesProps } from "../../components/locales/locales";
import {
  ISeoProps,
  getSeoMetaImage,
  resolveSeoImage,
} from "../../components/seo/seo";
import * as prismicHelper from "@prismicio/helpers";
import PageNavigation from "../../components/capability-navigation/page-navigation";
import { replaceWithDashes } from "../../utils/replaceWithDashes";
import {
  SharedSlice,
  SharedSliceFragment,
  SharedSliceType,
  WrapperType,
} from "../../components/shared-slice/shared-slice";
import { IPageContext, ILocationStateProps } from "../../types/types";
import { CustomerRfqCtaSliceComponent } from "../../components/customer-quote-request/customer-quote-request";

export const query = graphql`
  query StandardLandingPage($uid: String!, $lang: String!) {
    prismicStandardLandingPage(uid: { eq: $uid }, lang: { eq: $lang }) {
      lang
      _previewable
      uid
      type
      data {
        header_links {
          document {
            ... on PrismicHeaderLinks {
              uid
            }
          }
        }
        header_ctas {
          document {
            ... on PrismicHeaderCtas {
              uid
            }
          }
        }
        dark_mode
        announcement {
          document {
            ... on PrismicAnnouncement {
              data {
                content {
                  richText
                  text
                }
              }
            }
          }
        }
        no_index
        ...StandardLandingPageBreadcrumbs
        is_statsig_landing_page
        body {
          ...HeroLandingSlice
          ...HeroCenteredSlice
          ...TestimonialSlice
          ...ThreeSimpleCardsSlice
          ...FullWidthCtaSlice
          ...TextAndImageSlice
          ...ThreeSimpleCirclesSlice
          ...YoutubeVideoSlice
          ...NestedContentSlice
          ...TableSlice
          ...CapabilityListSlice
          ...ContentPostSlice
          ...ResourcesPostsSlice
          ...ImageGridSlice
          ...RichTextSlice
          ...BenefitQuadrantSlice
          ...MediaMarqueeSlice
          ...TestimonialsSlice
          ...ImageAndListSlice
          ...ImageCarouselSlice
          ...FeaturedCustomersSlice
          ...NewsletterSignupSlice
          ...CenteredFullWidthCtaSlice
          ...HeroFormSlice
          ...LinkGridSlice
          ...CarbonOffsetSlice
          ...AdvancedTableSlice
          ...ImageAndCaptionSlice
          ...NavigationTitleSlice
          ...QueriedResourcePostsSlice
          ...CountUpSlice
          ...PullQuoteSlice
          ...InlineCtaSlice
          ...XometryEverywhereSlice
          ...SimpleInlineCtaSlice
          ...TextAndModelFileSlice
          ...CustomerRfqCtaSlice
          ...LatestCapabilitiesSlice
          ...QuoteUploadComponentSlice
          ...HeroVideoSlice
          ...TextAndImageRowsSlice
          ...VideoCtaSlice
          ...ImageCtaSlice
          ...AdvancedSliceSlice
          ...HeroAuthorSlice
          ...HeroAdvancedSlice
          ...ContentCarouselSlice
          ...HeroLandingV2Slice
          ...TextAndIconsSlice
          ...CarouselCtaSlice
          ...TextAndFourColumnCardsSlice
          ...ComposableSliceSlice
          ...TabbedContentSlice
          ...CapabilitiesSlice
          ...CollapsibleSectionSlice
        }
        body1 {
          ...SeoSlice
          ...LocalesSlice
        }
      }
    }
  }
`;

const StandardLandingPage = (
  props: PageProps<StandardLandingPageQuery, IPageContext, ILocationStateProps>
) => {
  const standard_landing_page = props.data.prismicStandardLandingPage;
  const standard_landing_page_data = standard_landing_page?.data;
  const slices = standard_landing_page_data?.body;
  const darkMode = standard_landing_page_data?.dark_mode || false;
  const isStatsigLandingPage =
    standard_landing_page_data?.is_statsig_landing_page || false;

  const landingHero = slices?.find((slice) => {
    return "slice_type" in slice && slice?.slice_type == "landing_page_hero";
  }) as HeroLandingSliceFragment;

  const heroVideo = slices?.find((slice) => {
    return "slice_type" in slice && slice?.slice_type == "hero_video";
  }) as HeroVideoSliceFragment;

  const centeredHero = slices?.find((slice) => {
    return "slice_type" in slice && slice?.slice_type == "centered_hero";
  }) as HeroCenteredSliceFragment;

  const formHero = slices?.find((slice) => {
    return "slice_type" in slice && slice?.slice_type == "form_hero";
  }) as HeroFormSliceFragment;

  const heroAdvanced = slices?.find((slice) => {
    return "slice_type" in slice && slice?.slice_type == "hero_advanced";
  }) as HeroAdvancedSliceFragment;

  const landingHeroV2 = slices?.find((slice) => {
    return "slice_type" in slice && slice?.slice_type == "landing_page_hero_v2";
  }) as HeroLandingV2SliceFragment;

  const hero =
    landingHero ||
    centeredHero ||
    formHero ||
    heroVideo ||
    heroAdvanced ||
    landingHeroV2;

  // Form hero doesn't have an image field, so we have to be careful about accessing the image field on the hero object
  const getSEOImage = (): string | null | undefined =>
    hero && hero.slice_type !== "form_hero" && hero?.primary?.image
      ? hero.primary.image.url
      : null;
  const title = hero?.primary?.title;

  const seoSlices = standard_landing_page_data?.body1;
  let pageLocale: ILocalesProps = {};
  let seo: ISeoProps = {};
  let noIndex = standard_landing_page_data?.no_index || false;

  // Announcement Banner
  const announcement = standard_landing_page_data?.announcement
    ?.document as PrismicAnnouncement;
  const announcementData = announcement?.data as PrismicAnnouncementData;
  const announcementContent = announcementData?.content as PrismicRichTextField;
  const showAnnouncementBanner = announcement ? true : false;

  seoSlices?.forEach((slice) => {
    if (slice?.slice_type == "page_locales") {
      const pageLocales = slice as LocalesSliceFragment;
      pageLocale = {
        ...pageLocales,
        northAmericaHref: props.location.href,
      };
    } else if (slice?.slice_type == "seo_") {
      const seoSlice = slice as SeoSliceFragment;
      seo = {
        meta_title: seoSlice.primary?.meta_title,
        meta_description: seoSlice.primary?.meta_description,
        title: title,
        description:
          hero?.primary?.subtitle &&
          prismicHelper.asText(hero?.primary.subtitle.richText),
        // SEO slice meta_image overrides the hero image when set in Prismic.
        image: resolveSeoImage(
          getSeoMetaImage(seoSlice.primary),
          getSEOImage()
        ),
        lang: standard_landing_page?.lang,
      };
    }
  });

  if (noIndex) {
    seo = {
      ...seo,
      robots: "noindex",
    };
  }

  const headerLinksDoc = standard_landing_page_data?.header_links?.document;
  const headerLinksUID =
    headerLinksDoc && "uid" in headerLinksDoc && headerLinksDoc.uid;
  const hasExplicitHeaderLinks = Boolean(headerLinksUID);
  const headerCTAsDoc = standard_landing_page_data?.header_ctas?.document;
  const headerCTAsUID =
    headerCTAsDoc && "uid" in headerCTAsDoc && headerCTAsDoc.uid;

  function getBreadcrumb(
    breadcrumb?: StandardLandingPageBreadcrumbFragment | null
  ): BreadCrumbProps | null {
    const pageData =
      breadcrumb?.document &&
      "data" in breadcrumb.document &&
      (breadcrumb?.document?.data as PrismicStandardLandingPageData);
    if (pageData) {
      // Fallback to page data title if body?.[0]?.primary?.title is not available
      const title =
        (pageData.body?.[0] as HeroCenteredSliceFragment)?.primary?.title ||
        pageData.title;
      return {
        name: title || "Xometry",
        url: linkTo(breadcrumb),
      };
    }
    return null;
  }

  const getBreadcrumbs = () => {
    const result: BreadCrumbProps[] = [];
    let grandparent;
    const parent = standard_landing_page_data?.parent_id;
    if (parent?.document && "data" in parent.document) {
      grandparent = parent?.document?.data?.parent_id;
    }
    const selfLink = standard_landing_page && linkTo(standard_landing_page);

    const grandparentBreadcrumb = getBreadcrumb(grandparent);
    if (grandparentBreadcrumb) {
      result.push(grandparentBreadcrumb);
    }
    const parentBreadcrumb = getBreadcrumb(parent);
    if (parentBreadcrumb) {
      result.push(parentBreadcrumb);
    }
    selfLink &&
      title &&
      result.push({
        name: title,
        url: selfLink,
      });

    return result;
  };

  const breadcrumbs = getBreadcrumbs();
  const sectionRefs: React.RefObject<HTMLDivElement>[] = [];
  const navigationTitles: NavigationTitleSliceFragment[] = [];

  let heroComponent;
  const parseSlices = () => {
    return slices?.flatMap((slice, index) => {
      if (!("slice_type" in slice)) return;
      // Using a switch since it executes faster than large if-else blocks
      switch (slice.slice_type) {
        case "landing_page_hero":
          heroComponent = (
            <LandingHero
              key={index}
              hero={slice as HeroLandingSliceFragment}
              breadcrumbs={breadcrumbs}
            />
          );
          return [];
        case "hero_video":
          heroComponent = (
            <HeroVideo key={index} hero={slice as HeroVideoSliceFragment} />
          );
          return [];
        case "centered_hero":
          heroComponent = (
            <HeroCentered
              key={index}
              hero={(slice as HeroCenteredSliceFragment).primary}
              items={(slice as HeroCenteredSliceFragment).items}
              breadcrumbs={breadcrumbs}
            />
          );
          return [];
        case "form_hero":
          heroComponent = (
            <HeroForm
              key={index}
              hero={slice as HeroFormSliceFragment}
              breadcrumbs={breadcrumbs}
            />
          );
          return [];
        case "hero_advanced":
          heroComponent = (
            <HeroAdvanced
              key={index}
              hero={slice as HeroAdvancedSliceFragment}
            />
          );
          return [];
        case "customer_rfq_cta":
          return (
            <CustomerRfqCtaSliceComponent
              key={index}
              slice={slice as CustomerRfqCtaSlice}
            />
          );
        case "navigation_title":
          const sectionRef = createRef<HTMLDivElement>();
          const navSlice = slice as NavigationTitleSliceFragment;
          navigationTitles.push(navSlice);
          sectionRefs.push(sectionRef);
          return (
            <div
              key={index}
              id={`${replaceWithDashes(navSlice.primary.title1) || ""}-${
                props.pageContext.prismicId
              }`}
              ref={sectionRef}
            />
          );
        case "landing_page_hero_v2":
          heroComponent = (
            <LandingHeroV2
              key={index}
              hero={slice as HeroLandingV2SliceFragment}
            />
          );
          return [];
        default:
          if (slice.slice_type && slice.slice_type in SharedSliceType) {
            return (
              <SharedSlice
                context={props.pageContext}
                index={index}
                key={index}
                Wrappers={{
                  [WrapperType.narrow]: SliceWrapper,
                  [WrapperType.fullWidth]: FullWidthSliceWrapper,
                }}
                slice={slice as SharedSliceFragment}
                slice_type={
                  SharedSliceType[
                    slice.slice_type as keyof typeof SharedSliceType
                  ]
                }
              />
            );
          }
      }
    });
  };
  const pageContent = parseSlices();
  return (
    <Layout
      seo={seo}
      isGuttered={false}
      showAnnouncementBanner={showAnnouncementBanner}
      announcementContent={announcementContent}
      headerLinksUID={headerLinksUID || "default-links"}
      headerCTAsUID={headerCTAsUID || "default-ctas"}
      navExperimentEligible={!hasExplicitHeaderLinks}
      locales={pageLocale}
      darkMode={darkMode}
      isStatsigLandingPage={isStatsigLandingPage}
    >
      {heroComponent}
      {navigationTitles.length > 0 ? (
        <div className="grid container">
          <div className="side-col">
            <PageNavigation
              pageNavigation={navigationTitles}
              refs={sectionRefs}
            />
          </div>
          <div className="main-col">{pageContent}</div>
        </div>
      ) : (
        <div className="main-col-full">{pageContent}</div>
      )}
    </Layout>
  );
};

export function Head({ data }: HeadProps<StandardLandingPageQuery>) {
  if (!data.prismicStandardLandingPage?.data?.is_statsig_landing_page) {
    return null;
  }

  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
          (function () {
            var key = window.__env__ && window.__env__.STATSIG_API_KEY;
            if (!key) return;
            var s = document.createElement("script");
            s.src =
              "https://cdn.jsdelivr.net/npm/statsig-sidecar-v2-beta/dist/index.min.js?apikey=" +
              encodeURIComponent(key);
            document.head.appendChild(s);
          })();
        `,
      }}
    />
  );
}

export default withPrismicPreview(StandardLandingPage);
